import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Snackbar, Alert } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import { GetAll_stage_Data } from '../../Redux/Actions/Stage_Action';
import { StagesActions } from './StagesActions';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconSearch   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconFilter   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const IconCopy     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const IconPlus     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconLayers   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
const IconTag      = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
const IconTrend    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
const IconChevron  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>;
const IconX        = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconNote     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;

/* ─── Searchable Select ──────────────────────────────────────────────────── */
const SearchableSelect = ({ value, onChange, options, placeholder = 'All', isDark }) => {
  const [open,    setOpen]    = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const wrapRef               = React.useRef(null);

  const filteredOpts = options.filter((o) =>
    o.label.toLowerCase().includes(searchQ.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => { setOpen(!open); setSearchQ(''); }}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
          border: `1px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
          backgroundColor: isDark ? '#151827' : '#f8fafc',
          color: value === 'all' ? (isDark ? '#6b7280' : '#94a3b8') : (isDark ? '#e5e7eb' : '#1f2937'),
          fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxSizing: 'border-box', userSelect: 'none',
        }}
      >
        <span>{selected?.label || placeholder}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {value !== 'all' && (
            <span
              onClick={(e) => { e.stopPropagation(); onChange('all'); setOpen(false); }}
              style={{ display: 'flex', color: isDark ? '#6b7280' : '#94a3b8', padding: '1px' }}
            >
              <IconX />
            </span>
          )}
          <span style={{
            display: 'flex', color: isDark ? '#6b7280' : '#94a3b8',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s',
          }}>
            <IconChevron />
          </span>
        </span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 9999,
          backgroundColor: isDark ? '#1e2132' : '#ffffff',
          border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
          borderRadius: '10px', overflow: 'hidden',
          boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.5)' : '0 8px 30px rgba(0,0,0,0.12)',
        }}>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
              color: isDark ? '#6b7280' : '#94a3b8', display: 'flex', pointerEvents: 'none',
            }}>
              <IconSearch />
            </span>
            <input
              autoFocus
              placeholder="Search…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px 10px 34px', fontSize: '13px',
                outline: 'none', border: 'none',
                borderBottom: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
                backgroundColor: isDark ? '#151827' : '#f8fafc',
                color: isDark ? '#e5e7eb' : '#1f2937',
                fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filteredOpts.length === 0 ? (
              <div style={{ padding: '12px', fontSize: '13px', color: isDark ? '#6b7280' : '#94a3b8', textAlign: 'center' }}>
                No results
              </div>
            ) : (
              filteredOpts.map((o) => (
                <div
                  key={o.value}
                  onClick={() => { onChange(o.value); setOpen(false); setSearchQ(''); }}
                  style={{
                    padding: '9px 14px', fontSize: '13px', cursor: 'pointer',
                    backgroundColor: o.value === value
                      ? (isDark ? 'rgba(37,99,235,0.15)' : 'rgba(37,99,235,0.07)')
                      : 'transparent',
                    color: o.value === value
                      ? (isDark ? '#93c5fd' : '#1d4ed8')
                      : (isDark ? '#e5e7eb' : '#1f2937'),
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    if (o.value !== value)
                      e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
                  }}
                  onMouseLeave={(e) => {
                    if (o.value !== value) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const StageTable = () => {
  const dispatch     = useDispatch();
  const theme        = useTheme();
  const mode         = theme.palette.mode;
  const isDark       = mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  const [loading,      setLoading]      = useState(true);
  const [userlogin,    setUserlogin]    = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [typeFilter,   setTypeFilter]   = useState('all');
  const [showFilter,   setShowFilter]   = useState(false);
  const [copyOpen,     setCopyOpen]     = useState(false);
  const [page,         setPage]         = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const run = async () => {
    setLoading(true);
    await dispatch(GetAll_stage_Data());
    setLoading(false);
  };
  useEffect(() => { run(); }, [dispatch]);

  const All_stages = useSelector((state) => state.Allstages.stage);

  const allStages = Array.isArray(All_stages?.data)
    ? All_stages.data.filter((s) => s.name !== 'No Stage')
    : Array.isArray(All_stages)
      ? All_stages.filter((s) => s.name !== 'No Stage')
      : [];

  const uniqueTypes = [...new Set(allStages.map((s) => s.stagetype?.name).filter(Boolean))];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    ...uniqueTypes.map((t) => ({ value: t, label: t })),
  ];

  /* ── filter ── */
  const filtered = allStages.filter((stage) => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q ||
      stage.name?.toLowerCase().includes(q) ||
      stage.notes?.toLowerCase().includes(q) ||
      stage.stagetype?.name?.toLowerCase().includes(q);
    const matchType = typeFilter === 'all' || stage.stagetype?.name === typeFilter;
    return matchSearch && matchType;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageItems  = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  /* ── stats ── */
  const newestStage = allStages.length
    ? allStages.reduce((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? a : b)
    : null;

  const homeLink =
    userlogin?.data?.role === 'Admin'      ? '/AllLeadsForAdmin'
  : userlogin?.data?.role === 'Marketer'   ? '/ALLLeadsForMarketer'
  : userlogin?.data?.role === 'Accountant' ? '/LeadsForAccountant'
  : '/Dashboard';

  const clearFilters = () => { setSearchTerm(''); setTypeFilter('all'); setPage(1); };

  /* ── page numbers ── */
  const buildPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (page <= 4)                   pages.push(1, 2, 3, 4, 5, '...', totalPages);
    else if (page >= totalPages - 3) pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    else                             pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    return pages;
  };

  /* ── type badge color ── */
  const getTypeBadgeStyle = (typeName) => {
    const colors = [
      { bg: isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.08)', color: isDark ? '#a5b4fc' : '#4f46e5', border: isDark ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.2)' },
      { bg: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)', color: isDark ? '#6ee7b7' : '#059669', border: isDark ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.2)' },
      { bg: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.08)', color: isDark ? '#fcd34d' : '#d97706', border: isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)' },
      { bg: isDark ? 'rgba(239,68,68,0.12)'  : 'rgba(239,68,68,0.08)',  color: isDark ? '#fca5a5' : '#dc2626', border: isDark ? 'rgba(239,68,68,0.25)'  : 'rgba(239,68,68,0.2)'  },
      { bg: isDark ? 'rgba(14,165,233,0.12)' : 'rgba(14,165,233,0.08)', color: isDark ? '#7dd3fc' : '#0284c7', border: isDark ? 'rgba(14,165,233,0.25)' : 'rgba(14,165,233,0.2)' },
    ];
    if (!typeName) return colors[0];
    const idx = typeName.charCodeAt(0) % colors.length;
    return colors[idx];
  };

  const filterLabel = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: isDark ? '#6b7280' : '#94a3b8',
    marginBottom: '5px', display: 'block',
  };

  /* ─── render ─── */
  return (
    <>
      <style>{`
        .st-row:hover { background-color: ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'} !important; }
        .st-action-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; height: 30px; border-radius: 8px; border: none; cursor: pointer;
          background: ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
          color: ${isDark ? '#9ca3af' : '#6b7280'}; transition: all 0.15s;
        }
        .st-action-btn:hover {
          background: ${isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)'};
          color: ${isDark ? '#a5b4fc' : '#0D47A1'};
        }
        .st-create-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 10px; border: none; cursor: pointer; font-size: 13px;
          font-weight: 600; font-family: 'DM Sans',sans-serif;
          background: linear-gradient(135deg,#0D47A1,#0D47A1);
          color: #fff; transition: all 0.2s; white-space: nowrap;
          box-shadow: 0 2px 8px #0D47A1;
        }
        .st-create-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 14px #0D47A1; }
        .st-type-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 10px; cursor: pointer; font-size: 13px;
          font-weight: 600; font-family: 'DM Sans',sans-serif;
          background: ${isDark ? 'rgba(255,255,255,0.06)' : '#fff'};
          color: ${isDark ? '#e5e7eb' : '#374151'};
          border: 1px solid ${isDark ? '#2a2f45' : '#e2e8f0'};
          transition: all 0.2s; white-space: nowrap;
        }
        .st-type-btn:hover { background: ${isDark ? 'rgba(255,255,255,0.1)' : '#f8fafc'}; }
        .st-filter-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 10px; border: none; cursor: pointer; font-size: 13px;
          font-weight: 600; font-family: 'DM Sans',sans-serif;
          background: ${isDark ? 'rgba(255,255,255,0.06)' : '#fff'};
          color: ${isDark ? '#9ca3af' : '#6b7280'};
          border: 1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}; transition: all 0.15s;
        }
        .st-filter-btn:hover { background: ${isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'}; }
        .st-search-inline {
          display: flex; align-items: center; gap: 8px;
          padding: 8px 14px; border-radius: 10px;
          background: ${isDark ? 'rgba(255,255,255,0.05)' : '#fff'};
          border: 1px solid ${isDark ? '#2a2f45' : '#e2e8f0'};
          color: ${isDark ? '#9ca3af' : '#94a3b8'};
        }
        .st-search-inline input {
          border: none; outline: none; background: transparent; font-size: 13px;
          font-family: 'DM Sans',sans-serif; color: ${isDark ? '#e5e7eb' : '#1f2937'};
          width: 220px;
        }
        .st-search-inline input::placeholder { color: ${isDark ? '#4b5563' : '#94a3b8'}; }
        .st-clear-btn {
          padding: '6px 14px'; border-radius: '8px'; border: 'none'; cursor: 'pointer';
          font-size: '12px'; font-weight: 600; font-family: 'DM Sans',sans-serif;
          background: transparent; color: ${isDark ? '#9ca3af' : '#6b7280'};
          border: 1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}; transition: all 0.15s;
        }
        .st-clear-btn:hover { background: ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}; }
        .st-checkbox {
          width: 15px; height: 15px; border-radius: 4px; cursor: pointer; accent-color: #0D47A1;
        }
        .st-pag-btn {
          min-width: 32px; height: 32px; border-radius: 8px; border: none; cursor: pointer;
          font-size: 13px; font-family: 'DM Sans',sans-serif; font-weight: 500;
          background: transparent; color: ${isDark ? '#9ca3af' : '#6b7280'}; transition: all 0.15s;
          display: inline-flex; align-items: center; justify-content: center; padding: 0 6px;
        }
        .st-pag-btn:hover:not(:disabled) { background: ${isDark ? 'rgba(99,102,241,0.15)' : 'rgba(37,99,235,0.08)'}; color: ${isDark ? '#a5b4fc' : '#1d4ed8'}; }
        .st-pag-btn.active { background: #0D47A1; color: #fff; font-weight: 700; }
        .st-pag-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        .st-pag-ellipsis { color: ${isDark ? '#4b5563' : '#94a3b8'}; padding: 0 4px; font-size: 14px; }
        .st-page-select {
          padding: '4px 8px'; border-radius: '6px'; font-size: '12px'; border: '1px solid';
          background: ${isDark ? '#151827' : '#fff'};
          border: 1px solid ${isDark ? '#2a2f45' : '#e2e8f0'};
          color: ${isDark ? '#e5e7eb' : '#1f2937'};
          font-family: 'DM Sans',sans-serif; cursor: pointer; padding: 4px 8px;
        }
        /* Stat Cards */
        .st-stat-card {
          flex: 1; min-width: 200px; padding: 20px 22px; border-radius: 16px;
          display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
        }
        .st-stat-card.light {
          background: ${isDark ? '#1a1d2e' : '#ffffff'};
          border: 1px solid ${isDark ? '#2a2f45' : '#e8eaf0'};
          box-shadow: ${isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)'};
        }
        .st-stat-card.dark-card {
          background: linear-gradient(135deg, #0D47A1 0%, #0D47A1 60%, #0D47A1 100%);
          border: none; box-shadow: 0 4px 20px rgba(37,99,235,0.35);
        }
        .st-stat-label { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 6px; color: ${isDark ? '#6b7280' : '#94a3b8'}; }
        .st-stat-label.white { color: rgba(255,255,255,0.7); }
        .st-stat-value { font-size: 28px; font-weight: 800; letter-spacing: -0.5px; color: ${isDark ? '#f9fafb' : '#111827'}; font-family: 'DM Sans',sans-serif; }
        .st-stat-value.white { color: #ffffff; }
        .st-stat-badge { display: inline-flex; align-items: center; gap: 4px; padding: '3px 8px'; border-radius: '20px'; font-size: '11px'; font-weight: 600; margin-top: '10px'; }
        .st-stat-badge.green { background: ${isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.1)'}; color: ${isDark ? '#6ee7b7' : '#059669'}; padding: 3px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; margin-top: 10px; display: inline-flex; }
        .st-badge-pill { background: rgba(255,255,255,0.2); color: #fff; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; padding: 3px 8px; border-radius: 20px; }
        .st-stat-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .st-stat-icon.light-icon { background: ${isDark ? 'rgba(37,99,235,0.12)' : 'rgba(37,99,235,0.08)'}; color: ${isDark ? '#93c5fd' : '#2563eb'}; }
        .st-stat-icon.dark-icon { background: rgba(255,255,255,0.15); color: #fff; }
        /* Table */
        .st-table { width: 100%; border-collapse: collapse; font-family: 'DM Sans',sans-serif; }
        .st-table thead tr { border-bottom: 1px solid ${isDark ? '#2a2a45' : '#e8eaf0'}; }
        .st-table th {
          padding: 11px 16px; text-align: left; font-size: 11px; font-weight: 700;
          letter-spacing: 0.07em; text-transform: uppercase;
          color: ${isDark ? '#4b5563' : '#94a3b8'};
          background: ${isDark ? '#1a1a2e' : '#ffffff'}; white-space: nowrap;
        }
        .st-table td { padding: 13px 16px; font-size: 13px; color: ${isDark ? '#d1d5db' : '#374151'}; border-bottom: 1px solid ${isDark ? '#1e2035' : '#f1f3f8'}; vertical-align: middle; }
        .st-stage-cell { display: flex; align-items: center; gap: 10px; }
        .st-stage-thumb {
          width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center;
          justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0;
          background: linear-gradient(135deg, #0D47A1, #0D47A1); color: #fff;
        }
        .st-stage-name { font-weight: 600; color: ${isDark ? '#f3f4f6' : '#111827'}; font-size: 13px; }
        .st-cell-muted { color: ${isDark ? '#6b7280' : '#9ca3af'} !important; }
        .st-note-cell { max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: ${isDark ? '#9ca3af' : '#6b7280'} !important; font-size: 12px !important; }
        .st-empty { text-align: center; padding: 60px 20px !important; color: ${isDark ? '#4b5563' : '#94a3b8'} !important; }
        .st-spinner {
          width: 28px; height: 28px; border: 3px solid ${isDark ? '#2a2f45' : '#e2e8f0'};
          border-top-color: #0D47A1; border-radius: 50%; animation: st-spin 0.7s linear infinite;
          margin: 0 auto 10px;
        }
        @keyframes st-spin { to { transform: rotate(360deg); } }
        /* Table section card */
        .st-table-section {
          background: ${isDark ? '#1a1a2e' : '#ffffff'};
          border-radius: 18px;
          border: 1px solid ${isDark ? '#2a2a45' : '#e8eaf0'};
          box-shadow: ${isDark ? 'none' : '0 2px 16px rgba(0,0,0,0.06)'};
          overflow: hidden; display: flex; flex-direction: column;
        }
        .st-table-header {
          padding: 18px 20px; display: flex; align-items: center;
          justify-content: space-between; flex-wrap: wrap; gap: 12px;
          border-bottom: 1px solid ${isDark ? '#2a2a45' : '#f1f3f8'};
        }
        .st-table-title-label { font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: ${isDark ? '#4b5563' : '#94a3b8'}; margin-bottom: 2px; }
        .st-table-title { font-size: 18px; font-weight: 800; color: ${isDark ? '#f9fafb' : '#111827'}; letter-spacing: -0.3px; }
        .st-table-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .st-table-wrap { flex: 1; overflow-y: auto; overflow-x: auto; }
        .st-pagination {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 16px; flex-wrap: wrap; gap: 10px;
        }
        .st-pagination-info { display: flex; align-items: center; gap: 10px; font-size: 12px; color: ${isDark ? '#6b7280' : '#9ca3af'}; font-family: 'DM Sans',sans-serif; }
        .st-pagination-btns { display: flex; align-items: center; gap: 4px; }
      `}</style>

      <div style={{
        position: 'fixed',
        top: '50px',
        left: sidebarWidth,
        right: 0,
        bottom: 0,
        transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: isDark ? '#0f1117' : '#f0f2f5',
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
      }}>

        {/* ── Breadcrumb ── */}
        <div style={{
          padding: '14px 28px', flexShrink: 0,
          backgroundColor: isDark ? '#1a1d2e' : '#f0f2f5',
          display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
        }}>
          <Link to={homeLink} style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>
            Home
          </Link>
          <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
          <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Stages</span>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Stats */}
          <section style={{ padding: '24px 28px 0', flexShrink: 0, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div className="st-stat-card light">
              <div>
                <div className="st-stat-label">TOTAL STAGES</div>
                <div className="st-stat-value">{allStages.length}</div>
                <div className="st-stat-badge green">↑ Active in pipeline</div>
              </div>
              <div className="st-stat-icon light-icon"><IconLayers /></div>
            </div>

            <div className="st-stat-card light">
              <div>
                <div className="st-stat-label">STAGE TYPES</div>
                <div className="st-stat-value">{uniqueTypes.length}</div>
                <div className="st-stat-badge green">Distinct categories</div>
              </div>
              <div className="st-stat-icon light-icon"><IconTag /></div>
            </div>

            <div className="st-stat-card dark-card">
              <div>
                <div className="st-stat-label white">LATEST STAGE</div>
                <div className="st-stat-value white" style={{ fontSize: 18, marginTop: 4 }}>
                  {newestStage?.name || '—'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                  <span className="st-badge-pill">MOST RECENT</span>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>
                    {newestStage ? moment(newestStage.createdAt).format('MMM DD, YYYY') : ''}
                  </span>
                </div>
              </div>
              <div className="st-stat-icon dark-icon"><IconTrend /></div>
            </div>
          </section>

          {/* Table Section */}
          <section
            className="st-table-section"
            style={{ margin: '24px 28px', flex: 1, overflow: 'hidden', minHeight: 0 }}
          >
            {/* header */}
            <div className="st-table-header">
              <div>
                <div className="st-table-title-label">PIPELINE</div>
                <div className="st-table-title">STAGES</div>
              </div>
              <div className="st-table-actions">
                <div className="st-search-inline">
                  <IconSearch />
                  <input
                    placeholder="Search by name, type or notes…"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  />
                </div>
                <button className="st-filter-btn" onClick={() => setShowFilter(!showFilter)}>
                  <IconFilter /> Filter
                </button>
                <Link to="/AddStages" style={{ textDecoration: 'none' }}>
                  <button className="st-create-btn" style={{background:'#0D47A1'}}><IconPlus /> Add Stage</button>
                </Link>
                <Link to="/ShowAllstageTypes" style={{ textDecoration: 'none' }}>
                  <button className="st-type-btn"><IconPlus /> Add Type</button>
                </Link>
              </div>
            </div>

            {/* Filter Row */}
            {showFilter && (
              <div style={{
                flexShrink: 0, padding: '16px 20px',
                backgroundColor: isDark ? '#161929' : '#f8fafc',
                borderBottom: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
                  <div>
                    <label style={filterLabel}>Filter by Type</label>
                    <SearchableSelect
                      value={typeFilter}
                      onChange={(v) => { setTypeFilter(v); setPage(1); }}
                      options={typeOptions}
                      placeholder="All Types"
                      isDark={isDark}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
                  <span style={{ fontSize: '12px', color: isDark ? '#6b7280' : '#94a3b8' }}>
                    Showing{' '}
                    <strong style={{ color: isDark ? '#e5e7eb' : '#1f2937' }}>{filtered.length}</strong>
                    {' '}of{' '}
                    <strong style={{ color: isDark ? '#e5e7eb' : '#1f2937' }}>{allStages.length}</strong> stages
                  </span>
                  <button
                    onClick={clearFilters}
                    style={{
                      padding: '6px 14px', borderRadius: '8px', border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
                      background: 'transparent', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                      fontFamily: "'DM Sans',sans-serif", color: isDark ? '#9ca3af' : '#6b7280', transition: 'all 0.15s',
                    }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="st-table-wrap">
              <table className="st-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}><input type="checkbox" className="st-checkbox" /></th>
                    <th>STAGE NAME</th>
                    <th>TYPE</th>
                    <th>CREATED DATE</th>
                    <th>NOTES</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="st-empty">
                        <div className="st-spinner" />
                        <span>Loading stages…</span>
                      </td>
                    </tr>
                  ) : pageItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="st-empty">
                        <div style={{ marginBottom: '8px', opacity: 0.4 }}><IconLayers /></div>
                        <p style={{ margin: 0 }}>
                          {searchTerm || typeFilter !== 'all' ? 'No stages match your filters' : 'No stages found'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((item, i) => {
                      const badgeStyle = getTypeBadgeStyle(item.stagetype?.name);
                      return (
                        <tr key={item._id || i} className="st-row">
                          <td><input type="checkbox" className="st-checkbox" /></td>
                          <td>
                            <div className="st-stage-cell">
                              <div className="st-stage-thumb">
                                {item.name?.slice(0, 2).toUpperCase() || 'ST'}
                              </div>
                              <span className="st-stage-name">{item.name || '—'}</span>
                            </div>
                          </td>
                          <td>
                            {item.stagetype?.name ? (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                                backgroundColor: badgeStyle.bg, color: badgeStyle.color,
                                border: `1px solid ${badgeStyle.border}`,
                              }}>
                                {item.stagetype.name}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="st-cell-muted" style={{ fontSize: 13 }}>
                            {item.createdAt ? moment(item.createdAt).format('MMM DD, YYYY') : '—'}
                          </td>
                          <td>
                            {item.notes ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ color: isDark ? '#6b7280' : '#94a3b8', display: 'flex' }}><IconNote /></span>
                                <span className="st-note-cell">{item.notes}</span>
                              </div>
                            ) : (
                              <span style={{ color: isDark ? '#374151' : '#d1d5db' }}>—</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <CopyToClipboard
                                text={item._id}
                                onCopy={() => { setCopyOpen(true); setTimeout(() => setCopyOpen(false), 2000); }}
                              >
                                <button className="st-action-btn" title="Copy ID"><IconCopy /></button>
                              </CopyToClipboard>
                              <StagesActions stage={item} allstages={run} />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtered.length > 0 && (
              <div
                className="st-pagination"
                style={{
                  backgroundColor: isDark ? '#1a1a2e' : '#ffffff',
                  borderTop: `1px solid ${isDark ? '#2a2a45' : '#e8eaf0'}`,
                  boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
                }}
              >
                <div className="st-pagination-info">
                  <span>Items per page:</span>
                  <select
                    className="st-page-select"
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
                  >
                    {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span>
                    Showing <strong>{(page - 1) * itemsPerPage + 1}–{Math.min(page * itemsPerPage, filtered.length)}</strong>
                    {' '}of <strong>{filtered.length}</strong> stages
                  </span>
                </div>
                <div className="st-pagination-btns">
                  <button className="st-pag-btn" onClick={() => setPage(1)} disabled={page === 1}>⟨|</button>
                  <button className="st-pag-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                  {buildPageNumbers().map((n, idx) =>
                    n === '...'
                      ? <span key={`e-${idx}`} className="st-pag-ellipsis">…</span>
                      : <button key={n} className={`st-pag-btn ${page === n ? 'active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                  )}
                  <button className="st-pag-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
                  <button className="st-pag-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>|⟩</button>
                </div>
              </div>
            )}
          </section>

          {/* Footer */}
          <footer style={{ padding: '16px 28px', flexShrink: 0, fontSize: '12px', color: isDark ? '#374151' : '#d1d5db' }}>
            <span>© 2026 Realatix CRM. Smart CRM that connects customers, data, and sales in one safe place.</span>
          </footer>
        </div>
      </div>

      <Snackbar open={copyOpen} autoHideDuration={2000} onClose={() => setCopyOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success" sx={{ width: '100%' }}>Copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
};
